import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppHeaderLayout from '../components/AppHeaderLayout';
import Spinner from '../components/Spinner';
import GalleryCard from '../components/Channel/GalleryCard';
import CommonModalLayout from '../components/Layouts/CommonModalLayout';
import {
    useCatalogQuota,
    useCatalogUploads,
    useDeleteCatalogUpload,
    useChannelProfile,
    useUpdateChannelProfile,
    uploadFileToS3,
    createSlotsInvoice,
} from '../api/uploads';
import { buttonStyles, primaryButtonStyles } from '../styles/commonStyles';
import { Trash2, X, AlertCircle, Plus, Pencil, Check, Camera } from 'lucide-react';
import { BsPinterest, BsTelegram } from 'react-icons/bs';
import { FaVk, FaOdnoklassniki } from 'react-icons/fa';
import { SiMaildotru } from 'react-icons/si';
import AddPostcardModal from '../components/Modals/AddPostcardModal';
import EditPostcardModal from '../components/Modals/EditPostcardModal';

const SOCIAL_PLATFORMS = [
    { key: 'telegram', label: 'Telegram', icon: BsTelegram, color: '#229ED9', placeholder: '@username или ссылка' },
    { key: 'pinterest', label: 'Pinterest', icon: BsPinterest, color: '#E60023', placeholder: 'pinterest.com/username' },
    { key: 'vk', label: 'VK', icon: FaVk, color: '#4C75A3', placeholder: 'vk.com/id или ссылка' },
    { key: 'ok', label: 'OK', icon: FaOdnoklassniki, color: '#EE8208', placeholder: 'ok.ru/profile или ссылка' },
    { key: 'mymail', label: 'Мой мир', icon: SiMaildotru, color: '#168DE2', placeholder: 'my.mail.ru/... или ссылка' },
];

function SocialBadges({ socialLinks, onEdit }) {
    return (
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {SOCIAL_PLATFORMS.map(({ key, label, icon: Icon, color }) => {
                const value = socialLinks?.[key];
                const isSet = !!value;
                return (
                    <button
                        key={key}
                        onClick={onEdit}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                            isSet
                                ? 'opacity-100 shadow-sm'
                                : 'opacity-30 hover:opacity-50'
                        }`}
                        style={isSet ? { backgroundColor: color + '20', color: color } : {}}
                        title={isSet ? `${label}: ${value}` : `${label} (не указан)`}
                    >
                        <Icon size={14} className={isSet ? '' : 'text-gray-400 dark:text-gray-500'} />
                    </button>
                );
            })}
            <button
                onClick={onEdit}
                className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="Редактировать соц. сети"
            >
                <Pencil size={11} />
            </button>
        </div>
    );
}

function SocialLinksModal({ isOpen, onClose, socialLinks, onSave }) {
    const [links, setLinks] = useState(() => {
        const init = {};
        SOCIAL_PLATFORMS.forEach(p => { init[p.key] = socialLinks?.[p.key] || ''; });
        return init;
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(links);
            onClose();
        } catch (e) {
            console.error('Save social links error:', e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <CommonModalLayout isOpen={isOpen} onClose={onClose} title="Социальные сети">
            <div className="space-y-3">
                {SOCIAL_PLATFORMS.map(({ key, label, icon: Icon, color, placeholder }) => (
                    <div key={key}>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            <Icon size={16} style={{ color }} />
                            {label}
                        </label>
                        <input
                            type="text"
                            value={links[key]}
                            onChange={(e) => setLinks(prev => ({ ...prev, [key]: e.target.value }))}
                            placeholder={placeholder}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                        />
                    </div>
                ))}
            </div>
            <div className="mt-4 flex gap-2">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`flex-1 ${primaryButtonStyles} py-2.5 disabled:opacity-50`}
                >
                    {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button
                    onClick={onClose}
                    className={buttonStyles}
                >
                    Отмена
                </button>
            </div>
        </CommonModalLayout>
    );
}

function PlansModal({ isOpen, onClose, quotaUsed, quotaTotal, onBuySlots, isBuyingSlots }) {
    return (
        <CommonModalLayout isOpen={isOpen} onClose={onClose} title="Выберите план">
            <p className="text-center text-xs text-gray-400 dark:text-gray-500 mb-3 -mt-2">
                Сейчас: {quotaUsed} / {quotaTotal} слотов
            </p>
            <div className="flex gap-2">
                {/* Free */}
                <div className="flex-1 rounded-xl p-3 bg-gray-50 dark:bg-gray-700/50 text-center">
                    <div className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-0.5">Free</div>
                    <div className="text-[11px] text-gray-400 mb-2">5 слотов</div>
                    <div className="text-xl font-extrabold text-gray-900 dark:text-gray-100">0</div>
                    <div className="text-[10px] text-gray-400 mb-2">бесплатно</div>
                    <ul className="text-[11px] text-left space-y-1 mb-3 text-gray-700 dark:text-gray-300">
                        <li className="flex items-center gap-1"><span className="text-green-500 font-bold">✓</span> 5 слотов</li>
                        <li className="flex items-center gap-1"><span className="opacity-30">—</span><span className="opacity-40">Без бейджа</span></li>
                        <li className="flex items-center gap-1"><span className="opacity-30">—</span><span className="opacity-40">Обычная очередь</span></li>
                    </ul>
                    <button className="w-full py-1.5 text-xs font-semibold rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-default">
                        Текущий
                    </button>
                </div>
                {/* Автор */}
                <div className="flex-1 rounded-xl p-3 border-2 border-amber-500 bg-gray-50 dark:bg-gray-700/50 text-center relative">
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap">
                        Популярный
                    </div>
                    <div className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-0.5">Автор</div>
                    <div className="text-[11px] text-gray-400 mb-2">50 слотов</div>
                    <div className="text-xl font-extrabold text-gray-900 dark:text-gray-100">100 ⭐</div>
                    <div className="text-[10px] text-gray-400 mb-2">/ месяц</div>
                    <ul className="text-[11px] text-left space-y-1 mb-3 text-gray-700 dark:text-gray-300">
                        <li className="flex items-center gap-1"><span className="text-green-500 font-bold">✓</span> 50 слотов</li>
                        <li className="flex items-center gap-1"><span className="text-green-500 font-bold">✓</span> Бейдж автора</li>
                        <li className="flex items-center gap-1"><span className="opacity-30">—</span><span className="opacity-40">Обычная очередь</span></li>
                    </ul>
                    <button
                        onClick={() => onBuySlots(50)}
                        disabled={isBuyingSlots}
                        className="w-full py-1.5 text-xs font-semibold rounded-lg bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 transition-colors"
                    >
                        Подписаться
                    </button>
                </div>
                {/* PRO */}
                <div className="flex-1 rounded-xl p-3 bg-gray-50 dark:bg-gray-700/50 text-center">
                    <div className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-0.5">PRO</div>
                    <div className="text-[11px] text-gray-400 mb-2">∞ безлимит</div>
                    <div className="text-xl font-extrabold text-gray-900 dark:text-gray-100">500 ⭐</div>
                    <div className="text-[10px] text-gray-400 mb-2">/ месяц</div>
                    <ul className="text-[11px] text-left space-y-1 mb-3 text-gray-700 dark:text-gray-300">
                        <li className="flex items-center gap-1"><span className="text-green-500 font-bold">✓</span> ∞ слотов</li>
                        <li className="flex items-center gap-1"><span className="text-green-500 font-bold">✓</span> Бейдж PRO</li>
                        <li className="flex items-center gap-1"><span className="text-green-500 font-bold">✓</span> Быстрая модерация</li>
                    </ul>
                    <button
                        onClick={() => onBuySlots(999)}
                        disabled={isBuyingSlots}
                        className="w-full py-1.5 text-xs font-semibold rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 transition-colors"
                    >
                        Подписаться
                    </button>
                </div>
            </div>
        </CommonModalLayout>
    );
}

function MyChannel() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: profile, isLoading: profileLoading } = useChannelProfile();
    const { data: quota, isLoading: quotaLoading } = useCatalogQuota();
    const { data: uploads, isLoading: uploadsLoading } = useCatalogUploads();
    const deleteUpload = useDeleteCatalogUpload();
    const updateProfile = useUpdateChannelProfile();

    const [uploadError, setUploadError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editName, setEditName] = useState('');
    const [editingUpload, setEditingUpload] = useState(null);
    const [isAvatarUploading, setIsAvatarUploading] = useState(false);
    const [isBuyingSlots, setIsBuyingSlots] = useState(false);
    const [showSlotsPopup, setShowSlotsPopup] = useState(false);
    const [showSocialModal, setShowSocialModal] = useState(false);
    const avatarInputRef = useRef(null);

    const isLoading = profileLoading || quotaLoading || uploadsLoading;

    if (isLoading) return <Spinner />;

    const channelName = profile?.name || 'Мой канал';
    const avatarUrl = profile?.avatar_url;
    const imagesCount = profile?.images_count || 0;
    const socialLinks = profile?.social_links || {};

    const quotaUsed = quota?.used || 0;
    const quotaTotal = quota?.total || 5;
    const quotaPercent = quotaTotal > 0 ? Math.round((quotaUsed / quotaTotal) * 100) : 0;
    const isQuotaFull = quotaUsed >= quotaTotal;

    const handleDelete = async (uploadId) => {
        if (deletingId) return;
        setDeletingId(uploadId);
        try {
            await deleteUpload.mutateAsync(uploadId);
        } catch (error) {
            console.error('Delete error:', error);
            setUploadError(error.message || 'Ошибка удаления');
        } finally {
            setDeletingId(null);
        }
    };

    const handleStartEditName = () => {
        setEditName(channelName);
        setIsEditingName(true);
    };

    const handleSaveName = async () => {
        const trimmed = editName.trim();
        if (trimmed.length < 2) {
            setUploadError('Имя должно быть не менее 2 символов');
            return;
        }
        try {
            await updateProfile.mutateAsync({ name: trimmed });
            setIsEditingName(false);
        } catch (error) {
            setUploadError(error.message || 'Ошибка обновления имени');
        }
    };

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setUploadError('Выберите изображение');
            return;
        }
        setIsAvatarUploading(true);
        try {
            const data = await uploadFileToS3(file);
            await updateProfile.mutateAsync({ avatar_url: data.cloud_url });
        } catch (error) {
            setUploadError(error.message || 'Ошибка загрузки аватарки');
        } finally {
            setIsAvatarUploading(false);
            if (avatarInputRef.current) avatarInputRef.current.value = '';
        }
    };

    const handleSaveSocialLinks = async (links) => {
        await updateProfile.mutateAsync({ social_links: links });
    };

    const uploadsList = uploads || [];

    const handleBuySlots = async (packSize) => {
        if (isBuyingSlots) return;
        setIsBuyingSlots(true);
        try {
            const res = await createSlotsInvoice(packSize);
            if (res.invoice_url) {
                const webApp = window.Telegram?.WebApp;
                if (webApp?.openInvoice) {
                    webApp.openInvoice(res.invoice_url, (status) => {
                        if (status === 'paid') {
                            window.location.reload();
                        }
                    });
                } else {
                    window.open(res.invoice_url, '_blank');
                }
            }
        } catch (error) {
            setUploadError(error.message || 'Ошибка создания платежа');
        } finally {
            setIsBuyingSlots(false);
        }
    };

    const rightContent = !isQuotaFull ? (
        <button
            onClick={() => setIsModalOpen(true)}
            className={buttonStyles}
        >
            <Plus size={16} className="inline-block mr-1" />
            Добавить
        </button>
    ) : null;

    return (
        <AppHeaderLayout
            rightContent={rightContent}
            title="Мой канал"
        >
            <div className="p-3 sm:p-4">
                {/* Profile header */}
                <div className="mb-4 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <button
                            onClick={() => avatarInputRef.current?.click()}
                            className="relative w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center overflow-hidden flex-shrink-0 group/avatar"
                            disabled={isAvatarUploading}
                        >
                            {avatarUrl ? (
                                <img
                                    src={avatarUrl}
                                    alt={channelName}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            ) : (
                                <span className="text-white text-xl font-bold">
                                    {channelName.charAt(0).toUpperCase()}
                                </span>
                            )}
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity rounded-full">
                                {isAvatarUploading ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <Camera size={18} className="text-white" />
                                )}
                            </div>
                        </button>
                        <input
                            ref={avatarInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleAvatarChange}
                        />

                        {/* Name + stats + social badges */}
                        <div className="flex-1 min-w-0">
                            {isEditingName ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value.slice(0, 50))}
                                        className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        autoFocus
                                        onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                                    />
                                    <button
                                        onClick={handleSaveName}
                                        className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600"
                                    >
                                        <Check size={14} />
                                    </button>
                                    <button
                                        onClick={() => setIsEditingName(false)}
                                        className="p-1.5 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                        {channelName}
                                    </h2>
                                    <button
                                        onClick={handleStartEditName}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                </div>
                            )}
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                {imagesCount} {imagesCount === 1 ? 'открытка' : imagesCount < 5 ? 'открытки' : 'открыток'}
                            </p>
                            <SocialBadges
                                socialLinks={socialLinks}
                                onEdit={() => setShowSocialModal(true)}
                            />
                        </div>
                    </div>
                </div>

                {/* Quota bar */}
                <div className="mb-4 bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                            Загружено
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {quotaUsed} / {quotaTotal}
                            </span>
                            <button
                                onClick={() => setShowSlotsPopup(true)}
                                className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-500 hover:bg-amber-600 text-white transition-colors shadow-sm"
                                title="Купить слоты"
                            >
                                <Plus size={14} strokeWidth={3} />
                            </button>
                        </div>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-300 ${
                                isQuotaFull
                                    ? 'bg-red-500'
                                    : quotaPercent > 70
                                        ? 'bg-amber-500'
                                        : 'bg-blue-500'
                            }`}
                            style={{ width: `${Math.min(quotaPercent, 100)}%` }}
                        />
                    </div>
                    {isQuotaFull && (
                        <p className="text-xs text-red-500 mt-1.5">
                            Лимит исчерпан.{' '}
                            <button
                                onClick={() => setShowSlotsPopup(true)}
                                className="underline hover:no-underline font-medium"
                            >
                                Купить ещё слотов
                            </button>
                        </p>
                    )}
                </div>

                {/* Error message */}
                {uploadError && (
                    <div className="mb-4 flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg p-3 text-sm">
                        <AlertCircle size={16} className="flex-shrink-0" />
                        <span className="flex-1">{uploadError}</span>
                        <button onClick={() => setUploadError(null)}>
                            <X size={14} />
                        </button>
                    </div>
                )}

                {/* Uploads grid */}
                {uploadsList.length === 0 ? (
                    <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                            <Plus size={32} className="opacity-40" />
                        </div>
                        <div className="text-lg font-semibold mb-1">Пока пусто</div>
                        <div className="text-sm">Нажмите «Добавить» чтобы загрузить открытку</div>
                    </div>
                ) : (
                    <div
                        className="grid gap-2 sm:gap-3"
                        style={{ gridTemplateColumns: 'repeat(auto-fill, 80px)', justifyContent: 'center' }}
                    >
                        {uploadsList.map(upload => (
                            <div key={upload.id} className="relative group">
                                <GalleryCard
                                    postcard={upload}
                                    onClick={() => profile?.source_id && navigate(`/source/${profile.source_id}/view/${upload.id}`)}
                                    className="w-[80px] h-[110px]"
                                />
                                {/* Edit button */}
                                <button
                                    onClick={(e) => { e.stopPropagation(); setEditingUpload(upload); }}
                                    className="absolute top-1 left-1 w-6 h-6 flex items-center justify-center
                                        bg-black/50 hover:bg-blue-600 rounded-full text-white
                                        opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                >
                                    <Pencil size={10} />
                                </button>
                                {/* Delete button */}
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(upload.id); }}
                                    disabled={deletingId === upload.id}
                                    className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center
                                        bg-black/50 hover:bg-red-600 rounded-full text-white
                                        opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                >
                                    {deletingId === upload.id ? (
                                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Trash2 size={12} />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                )}

            </div>

            <AddPostcardModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
            <EditPostcardModal
                isOpen={!!editingUpload}
                onClose={() => setEditingUpload(null)}
                upload={editingUpload}
            />
            <PlansModal
                isOpen={showSlotsPopup}
                onClose={() => setShowSlotsPopup(false)}
                quotaUsed={quotaUsed}
                quotaTotal={quotaTotal}
                onBuySlots={handleBuySlots}
                isBuyingSlots={isBuyingSlots}
            />
            <SocialLinksModal
                isOpen={showSocialModal}
                onClose={() => setShowSocialModal(false)}
                socialLinks={socialLinks}
                onSave={handleSaveSocialLinks}
            />
        </AppHeaderLayout>
    );
}

export default MyChannel;
